<template>
  <v-data-table
    :loading="loading"
    :headers="headers"
    :items="data"
    multi-sort
    :sort-by="['lastCorrectionAbsolutePercent', 'lastCorrectionDate']"
    :sort-desc="[true, true]"
    :items-per-page="itemPerPage > 0 ? itemPerPage : data.length + 1"
    hide-default-footer
    hide-default-header
    :mobile-breakpoint="0"
    dense
  >
    <template v-slot:item="{ item }">
      <tr>
        <td width="35%" class="product-td">
          <span class="caption">{{ item.name }}</span>
        </td>
        <td>
          <v-row
            align="center"
            dense
            v-for="corr in item.corrections"
            :key="corr.id"
            no-gutters
          >
            <v-col style="flex-grow:0; flex-shrink:1;">
              <span class="caption pr-1">{{
                corr.date | denseDateFormat
              }}</span>
            </v-col>
            <v-col style="flex-grow:1; flex-shrink:1;">
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <div v-on="on" class="progress-chart">
                    <v-progress-linear
                      color="#FFCDD2"
                      background-color="#E57373"
                      :background-opacity="1"
                      height="20px"
                      :value="100 - corr.percentNeg"
                    >
                      <template v-slot="{ value }">
                        <span v-if="value < 100">{{
                          Number(corr.quantity)
                        }}</span>
                        <span v-if="value < 100" class="caption ml-1">{{
                          item.unit
                        }}</span>
                      </template>
                    </v-progress-linear>
                    <v-progress-linear
                      color="#66BB6A"
                      background-color="#C8E6C9"
                      height="20px"
                      :value="corr.percent"
                    >
                      <template v-slot="{ value }">
                        <span v-if="value > 0">{{
                          Number(corr.quantity)
                        }}</span>
                        <span v-if="value > 0" class="caption ml-1">{{
                          item.unit
                        }}</span>
                      </template>
                    </v-progress-linear>
                  </div>
                </template>
                <div>
                  <v-row no-gutters>
                    <span>{{ corr.date | localFormat }}</span>
                  </v-row>
                  <v-row no-gutters>
                    <span>{{ `${corr.quantity} ${item.unit}` }}</span>
                  </v-row>
                </div>
              </v-tooltip>
            </v-col>
          </v-row>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";
import { api } from "@/api";
import moment from "moment";
import { round10 } from "round10";
export default {
  name: "CorrectionsChart",
  props: {
    start: {
      required: true
    },
    end: {
      required: true
    },
    itemPerPage: {
      type: Number,
      default: 0
    }
  },
  mounted() {
    this.getData();
  },
  computed: {
    ...mapGetters(["getProducts"])
  },
  data() {
    return {
      data: [],
      loading: false,
      headers: [{ value: "corrections", text: "" }]
    };
  },
  methods: {
    async getData() {
      if (this.start !== null) {
        try {
          this.loading = true;
          this.data = [];
          const rawData = await api.getInventoryRecordsCorrections({
            start:
              this.start != null
                ? moment(this.start)
                    .hours(0)
                    .minutes(0)
                    .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
                : this.start,
            end:
              this.end != null
                ? moment(this.end)
                    .hours(23)
                    .minutes(59)
                    .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
                : this.end
          });
          const newData = [];
          this.getProducts
            .filter(x => x.is_deleted === false && x.is_pinned === true)
            .forEach(p => {
              const correctionsForProduct = rawData.filter(
                d => d.fk_product === p.id && d.percent !== 0
              );
              if (correctionsForProduct.length > 0) {
                const correctionsDates = correctionsForProduct.map(c =>
                  moment(c.created)
                );
                const lastCorrectionDate = moment.max(correctionsDates);
                const lastCorrection = correctionsForProduct.find(
                  c => moment(c.created).isSame(lastCorrectionDate) === true
                );
                const d = {
                  fk_product: p.id,
                  name: p.nom,
                  unit: p.data ? p.data.unite_mesure_inventory : "",
                  lastCorrectionDate,
                  lastCorrection,
                  lastCorrectionPercent: lastCorrection.percent,
                  lastCorrectionAbsolutePercent: Math.abs(
                    lastCorrection.percent
                  ),
                  corrections: correctionsForProduct
                    .map(c => {
                      return {
                        id: c.id,
                        date: moment(c.created),
                        quantity: round10(Number(c.quantity), 0),
                        percent: c.percent > 0 ? Number(c.percent) * 100 : 0,
                        percentNeg: c.percent < 0 ? Number(c.percent) * -100 : 0
                      };
                    })
                    .sort((a, b) => b.date.diff(a.date))
                };
                newData.push(d);
              }
            });
          this.data = newData;
        } finally {
          this.loading = false;
        }
      }
    }
  },
  watch: {
    start: {
      immediate: true,
      handler: "getData"
    },
    getProducts: {
      deep: true,
      handler: "getData"
    },
    end(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.progress-chart {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
}
.product-td {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
